'use client';

import { useEffect, useState } from 'react';
import { Cookie, ShieldCheck, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Button } from '@/components/ui/button';
import { toMountedPath } from '@/lib/routing/host-router';
import { currentPlatformHost } from '@/lib/routing/platform-hosts';
import { isPlacePathname } from '@/lib/routing/place-href';
import { isPosRoute } from '@/lib/routing/route-registry';

const COOKIE_KEY = 'menuraq_cookie_consent';

/**
 * Consent is stored in a DOMAIN-scoped cookie, not localStorage — doc 32 §6.
 *
 * localStorage is per-origin, so once the app spanned www., dashboard., manage.,
 * pos. and m. the banner reappeared on each one and the visitor had to consent
 * three or four times crossing hosts. One consent covers menuraq.com and its
 * subdomains, so one cookie should carry it.
 *
 * Sharing this across hosts is deliberate and safe in a way sharing a SESSION is
 * not (doc 32 D4): a consent choice is a preference, not a credential. Nothing can
 * be impersonated with it, so the reasoning that keeps auth cookies host-scoped
 * does not apply.
 *
 * Falls back to localStorage off a menuraq.com host — localhost and preview
 * deployments cannot set a cookie for a domain they are not under.
 */
function readConsent(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COOKIE_KEY}=`));
  if (match) return decodeURIComponent(match.slice(COOKIE_KEY.length + 1));
  // Migration: anyone who consented before this shipped has it in localStorage.
  // Read it so they are not asked again.
  try {
    return localStorage.getItem(COOKIE_KEY);
  } catch {
    return null;
  }
}

function writeConsent(value: string): void {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const registrable = host.endsWith('menuraq.com') ? '.menuraq.com' : null;

  if (registrable) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // A year: long enough not to nag, short enough that consent is re-confirmed.
    document.cookie =
      `${COOKIE_KEY}=${encodeURIComponent(value)}; Domain=${registrable}; Path=/; ` +
      `Max-Age=${60 * 60 * 24 * 365}; SameSite=Lax${secure}`;
  }

  // Always mirror locally: it is the only store in dev, and it keeps the choice
  // if the cookie is later cleared by a privacy tool.
  try {
    localStorage.setItem(COOKIE_KEY, value);
  } catch {
    /* private mode */
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const rawPathname = usePathname();
  // Match the MOUNTED path. On dashboard.menuraq.com the browser says `/analytics`,
  // so every prefix below would miss and the banner would appear over the dashboard
  // and the menu editor — the exact surfaces it is written to stay off.
  const pathname = toMountedPath(rawPathname ?? '/', currentPlatformHost());

  const hideOnThisRoute =
    pathname?.startsWith('/dashboard') ||
    // The till is a staff workplace like the dashboard, and a shared device: the
    // banner sat over the staff-code pad's bottom keys on every new till (QA 2026-10-16).
    isPosRoute(pathname) ||
    pathname?.startsWith('/preview') ||
    pathname?.startsWith('/previews') ||
    // Unattended screens , a TV menu board or counter kiosk has nobody to
    // answer a consent prompt, so the banner would sit on the wall forever.
    pathname?.startsWith('/display/') ||
    pathname?.startsWith('/kiosk') ||
    pathname === '/m' ||
    isPlacePathname(pathname);

  useEffect(() => {
    if (!readConsent()) setVisible(true);
  }, []);

  const saveConsent = (value: 'accepted' | 'denied' | 'dismissed') => {
    writeConsent(value);
    setVisible(false);
    setShowDetails(false);
  };

  const shown = visible && !hideOnThisRoute;

  // The banner is fixed to the bottom of the viewport, which can overlap
  // page content (e.g. the signup form's submit button) on short viewports.
  // Reserve space for it at the document level while it's shown.
  useEffect(() => {
    document.body.classList.toggle('mnrq-cookie-banner-open', shown);
    return () => document.body.classList.remove('mnrq-cookie-banner-open');
  }, [shown]);

  if (!shown) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 28, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="pointer-events-none fixed inset-x-0 bottom-3 z-[70] px-3 sm:bottom-5 sm:px-6"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="pointer-events-auto mx-auto w-full max-w-5xl overflow-hidden rounded-3xl border border-[var(--dm-card-border)]/70 bg-card/95 shadow-[0_24px_80px_rgba(0,0,0,0.22)] backdrop-blur-2xl">
            <div className="h-px w-full bg-gradient-to-r from-transparent via-accent/60 to-transparent" />

            <div className="relative p-4 sm:p-6">
              <button
                onClick={() => saveConsent('dismissed')}
                className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-sunken)] hover:text-[var(--color-text-primary)]"
                aria-label="Dismiss cookie consent"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-6">
                <div className="flex gap-3 sm:gap-4">
                  <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[var(--dm-card-border)]/70 bg-[var(--color-bg-sunken)]/60">
                    <Cookie className="h-5 w-5 text-accent" />
                  </div>

                  <div className="space-y-2">
                    <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight sm:text-lg">
                      <ShieldCheck className="h-4 w-4 text-accent" />
                      Your privacy settings
                    </h3>
                    <p className="text-sm leading-6 text-[var(--color-text-secondary)]">
                      Menuraq uses cookies to keep the platform secure, remember your preferences, and improve performance.
                      You can accept all cookies or decline optional ones. See our{' '}
                      <Link href="/cookies" className="mnrq-link text-[var(--color-text-primary)]">
                        Cookie Policy
                      </Link>{' '}
                      and{' '}
                      <Link href="/privacy" className="mnrq-link text-[var(--color-text-primary)]">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1 text-xs text-[var(--color-text-secondary)]">
                      <span className="rounded-full border border-[var(--dm-card-border)]/70 bg-[var(--color-bg-sunken)]/60 px-2.5 py-1 text-[var(--color-text-primary)]">Essential cookies always on</span>
                      <span className="rounded-full border border-[var(--dm-card-border)]/70 bg-[var(--color-bg-sunken)]/60 px-2.5 py-1 text-[var(--color-text-primary)]">No dark patterns</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowDetails((prev) => !prev)}
                  >
                    {showDetails ? 'Hide details' : 'Show details'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="!text-[var(--color-text-secondary)]"
                    onClick={() => saveConsent('denied')}
                  >
                    Decline optional
                  </Button>
                  <Button type="button" size="sm" variant="accent" onClick={() => saveConsent('accepted')}>
                    Accept all
                  </Button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {showDetails ? (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 rounded-2xl border border-[var(--dm-card-border)]/70 bg-[var(--color-bg-sunken)]/40 p-3 sm:p-4">
                      <p className="text-xs leading-6 text-[var(--color-text-secondary)] sm:text-sm">
                        Essential cookies are required for authentication, session safety, and core functionality.
                        Optional cookies help with product analytics and performance insights.
                      </p>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
